import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesRealmanAvatarPictureOmniV2Params {
  /**
   * 输入图像URL
   */
  image_url: string;
  /**
   * 音频URL，用于数字人动画
   */
  audio_url: string;
  /**
   * 视频分辨率，可选参数
   */
  resolution?: string;
  /**
   * 帧率，可选参数，必须是正整数，推荐值为25或30
   */
  fps?: number;
  /**
   * 比特率，可选参数，必须是正整数
   */
  bitrate?: number;
}

export interface VolcesRealmanAvatarPictureOmniV2Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_realman_avatar_picture_omni_v2
 *
 * Volces RealMan Avatar Picture Omni V2 Generator
 * 
 * Digital human avatar picture generation with audio-driven animation (数字人口型驱动生成).
 * API Documentation: https://www.volcengine.com/docs/85128/1580770
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesRealmanAvatarPictureOmniV2 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesRealmanAvatarPictureOmniV2({
 *   // 参数见 VolcesRealmanAvatarPictureOmniV2Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesRealmanAvatarPictureOmniV2Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesRealmanAvatarPictureOmniV2(
  params: VolcesRealmanAvatarPictureOmniV2Params,
): Promise<VolcesRealmanAvatarPictureOmniV2Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_realman_avatar_picture_omni_v2',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesRealmanAvatarPictureOmniV2Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
